import React, { useState, useEffect } from "react";

import Select from "react-select";
import { Trans, useTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import {
    getReactSelectSmallStyling,
    getReactSelectTheme,
} from "../../utils/ReactSelectStyles";
import LookupsAPI from "../../API/lookups.api";
import InstructionNotification from "../Notifications/InstructionNotification";
import SimpleNotification from "../Notifications/SimpleNotification";

const ProductAccess = ({
    productAccess,
    selectedUsers,
    setSelectedUsers,
    mappedUserAccesses,
    setMappedUserAccesses,
}) => {
    const [users, setUsers] = useState([]);
    const [usersCount, setUsersCount] = useState(0);

    const { t } = useTranslation();

    const headers = [t("email"), t("affiliate"), t("permission"), t("remove")];
    const userAccess = {
        read: { label: t("read"), value: "Read" },
        write: {
            label: t("readWrite"),
            value: "Read/Write",
        },
    };

    useEffect(() => {
        if (productAccess) {
            fetchUsers();
        }
    }, [productAccess]);

    const fetchUsers = async () => {
        const result = await LookupsAPI.getInferiorUsers();
        const mappedAccesses = mapUserAccesses(productAccess);
        positionUsers(mappedAccesses, result);
    };

    const mapUserAccesses = (access) => {
        let mappedAccesses = access.reduce((accesses, currentAccess) => {
            accesses[currentAccess.userId] = currentAccess.write
                ? userAccess.write
                : userAccess.read;
            return accesses;
        }, {});

        setMappedUserAccesses(mappedAccesses);
        return mappedAccesses;
    };

    const positionUsers = (mappedAccess, users) => {
        const { selected, options } = users.reduce(
            (accum, current) => {
                const currentVal = {
                    label: current.email,
                    value: {
                        ...current,
                        value: { ...current.value, isDeleted: false },
                    },
                };
                mappedAccess[current.id]
                    ? accum.selected.push(currentVal)
                    : accum.options.push(currentVal);

                return accum;
            },
            {
                selected: [],
                options: [],
            }
        );

        setUsersCount(selected.length);
        setSelectedUsers(selected);
        setUsers(options);
    };

    const previousAccess = (userId) => {
        return productAccess?.find((access) => access.userId === userId);
    };

    const handleOnUsersChange = (user) => {
        if (user) {
            //Add the selected user to selectedUsers
            setSelectedUsers((prevSelectedUsers) => {
                const newSelectedUsers = [...prevSelectedUsers];
                if (previousAccess(user.value.id)) {
                    //if the user already exists in fetched accesses then set isDeleted to false
                    newSelectedUsers.find(
                        (stepUser) => stepUser.value.id === user.value.id
                    ).value.isDeleted = false;
                } else {
                    //else unshift the selected user to selected users
                    newSelectedUsers.unshift(user);
                }

                return newSelectedUsers;
            });

            //Remove the selected user from users options
            setUsers((prevUsers) => {
                return prevUsers.filter((singleUser) => {
                    const value = singleUser.value;
                    //filter user by id and return only different ids
                    if (value.id !== user.value.id) {
                        return singleUser;
                    }
                });
            });

            setUsersCount((prevUsersCount) => prevUsersCount + 1);
            setMappedUserAccesses({
                ...mappedUserAccesses,
                [user.value?.id]: userAccess.read,
            });
        }
    };

    const handleChangeAccessType = (accessType, index) => {
        const isWriteAccess = accessType.value === userAccess.write.value;

        setSelectedUsers((prevSelectedUsers) => {
            const newSelected = [...prevSelectedUsers];
            newSelected[index].value = {
                ...newSelected[index].value,
                read: true,
                write: isWriteAccess,
            };
            return [...newSelected];
        });

        //Renew access dictionary with new access
        setMappedUserAccesses((prevMappedAccesses) => {
            const userId = selectedUsers[index]?.value?.id;
            return {
                ...prevMappedAccesses,
                [userId]: isWriteAccess ? userAccess.write : userAccess.read,
            };
        });
    };

    const handleRemoveUser = (index) => {
        let removedUser = selectedUsers[index].value;

        //Remove removed user from list of selected users
        let newSelected = [...selectedUsers];
        if (previousAccess(removedUser?.id)) {
            //if the user already exists in fetched accesses then set isDeleted to true
            newSelected[index].value.isDeleted = true;
        } else {
            //else remove the user from the selectedUsers
            newSelected.splice(index, 1);
        }

        setSelectedUsers(newSelected);

        //Add removed user to users options
        setUsers((prevUsers) => {
            return [
                ...prevUsers,
                { label: removedUser.email, value: removedUser },
            ];
        });

        setUsersCount((prevUserCount) => prevUserCount - 1);
    };

    const renderHeader = () => {
        return (
            headers &&
            headers.map((header, index) => {
                return (
                    <td key={index}>
                        <div className="font-bold px-6 py-3 text-center text-xs text-gray-500 uppercase tracking-wider">
                            <div className="flex items-center justify-center">
                                <span className="ml-2">{header}</span>
                            </div>
                        </div>
                    </td>
                );
            })
        );
    };

    const renderSelectedUsers = () => {
        return selectedUsers.map((user, index) => {
            if (!user.value?.isDeleted) {
                return (
                    <tr key={index}>
                        <td>
                            <div className="flex items-center content-center">
                                <img
                                    src={
                                        user?.value?.image ||
                                        require("../../assets/images/no-avatar.png")
                                            .default
                                    }
                                    className={`${
                                        user?.value?.image ? "" : "opacity-40"
                                    } table-user-img ml-6`}
                                    alt="UserImage"
                                />
                                <div className=" py-3 whitespace-wrap">
                                    {user?.value?.email}
                                </div>
                            </div>
                        </td>
                        <td>
                            <div className="px-6 py-3 whitespace-wrap capitalize">
                                {user?.value?.organization?.name}
                            </div>
                        </td>
                        <td>
                            <div className="px-6 py-3 whitespace-wrap">
                                <Select
                                    options={Object.values(userAccess)}
                                    value={
                                        mappedUserAccesses[user?.value?.id] ||
                                        ""
                                    }
                                    styles={getReactSelectSmallStyling(false)}
                                    onChange={(selected) => {
                                        handleChangeAccessType(selected, index);
                                    }}
                                    theme={getReactSelectTheme}
                                />
                            </div>
                        </td>
                        <td>
                            <div className={"px-6 py-3 whitespace-wrap"}>
                                <button
                                    onClick={() => handleRemoveUser(index)}
                                    className="table-btn table-btn-orange m-0"
                                >
                                    <i className="fas fa-trash-alt" />
                                </button>
                            </div>
                        </td>
                    </tr>
                );
            }
        });
    };

    const renderTableBody = () => {
        return !isEmpty(
            selectedUsers.filter((user) => !user.value?.isDeleted)
        ) ? (
            renderSelectedUsers()
        ) : (
            <InstructionNotification
                colSpan={headers?.length}
                message={t("noAccess")}
            />
        );
    };

    const content = [
        {
            required: true,
            label: <Trans i18nKey="users">Users</Trans>,
            input: (
                <Select
                    value=""
                    options={users}
                    isClearable
                    onChange={handleOnUsersChange}
                    className="z-12"
                    placeholder={t("selectPlaceholder", {
                        entity: t("users"),
                    })}
                    theme={getReactSelectTheme}
                />
            ),
        },
        {
            element: (
                <SimpleNotification message={t("partnerAdminsAccessNote")} />
            ),
        },
        {
            label: (
                <>
                    <Trans i18nKey="giveAccessTo">Give Access To</Trans>
                    <span className="text-primary-orange ml-1">
                        ({usersCount})
                    </span>
                </>
            ),
        },
        {
            element: (
                <div className="h-small-with-scroll table-responsive shadow overflow-hidden border-b border-gray-200 sm:rounded-lg small-scroll-x small-scroll-y">
                    <table className="table-striped min-w-full divide-y divide-gray-200 text-xs text-center w-full">
                        <thead className="parent-head">
                            <tr>{renderHeader()}</tr>
                        </thead>
                        <tbody>{renderTableBody()}</tbody>
                    </table>
                </div>
            ),
        },
    ];

    const renderContent = () => {
        return (
            content &&
            content.map((field, index) => {
                if (field.element) {
                    return (
                        <React.Fragment key={index}>
                            {field.element}
                        </React.Fragment>
                    );
                }

                return (
                    <div className="input-group" key={index}>
                        <label className="uppercase">{field.label}</label>
                        {field.input}
                    </div>
                );
            })
        );
    };

    return (
        <>
            <div className="main-customer-form-inputs">{renderContent()}</div>
        </>
    );
};

export default ProductAccess;
